import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ScienceHeader } from "@/components/ScienceHeader";
import { SmartImage } from "@/components/SmartImage";
import { getScienceIcon, SCIENCE_DISCLAIMER } from "@/data/science";
import { fetchScience } from "@/lib/api";
import { ExternalLink, Check, ShoppingBag } from "lucide-react";

export default function ScienceElement() {
  const { slug } = useParams();
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    let mounted = true;
    fetchScience()
      .then((d) => mounted && setArticles(d))
      .catch(() => mounted && setArticles([]));
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (articles === null) {
    return (
      <div className="min-h-screen bg-background">
        <ScienceHeader />
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="mt-4 h-10 w-72" />
          <Skeleton className="mt-6 h-40 w-full" />
        </div>
      </div>
    );
  }

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background">
        <ScienceHeader />
        <div className="mx-auto max-w-2xl px-4 py-24 text-center">
          <h1 className="font-display text-2xl font-semibold text-foreground">Hindi mahanap ang element</h1>
          <p className="mt-2 text-muted-foreground">Wala kaming article para sa paksang ito.</p>
          <Button asChild className="mt-6" data-testid="science-notfound-all">
            <Link to="/science">View all science</Link>
          </Button>
        </div>
      </div>
    );
  }

  const Icon = getScienceIcon(article.icon || article.name);
  const related = articles.filter((a) => a.slug !== slug && a.group === article.group).slice(0, 3);

  return (
    <div className="min-h-screen bg-background">
      <ScienceHeader />

      <section className="border-b border-border bg-secondary/30">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:py-16">
          <Link to="/science" className="text-sm font-medium text-accent hover:underline" data-testid="science-all-link">
            Science &middot; all elements
          </Link>
          <div className="mt-4 flex items-start gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/12 text-primary">
              <Icon className="h-7 w-7" />
            </span>
            <div>
              <span className="inline-flex items-center rounded-full bg-secondary px-3 py-0.5 text-xs font-medium text-primary">{article.group}</span>
              <h1 className="font-display mt-2 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl" data-testid="science-title">{article.name}</h1>
              <p className="mt-2 max-w-2xl text-base text-muted-foreground">{article.tagline}</p>
            </div>
          </div>
          {article.image ? (
            <div className="mt-8 aspect-[16/9] w-full overflow-hidden rounded-2xl border border-border bg-secondary/40" data-testid="science-hero-image">
              <SmartImage src={article.image} alt={article.name} fit="contain" fallbackLabel={article.name} />
            </div>
          ) : null}
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        {article.intro ? <p className="text-lg leading-relaxed text-foreground/90">{article.intro}</p> : null}

        {(article.gallery || []).length > 0 && (
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="science-gallery">
            {article.gallery.map((src, i) => (
              <figure
                key={i}
                className="group overflow-hidden rounded-2xl border border-border bg-secondary/40"
                data-testid={`science-gallery-image-${i}`}
              >
                <div className="aspect-[4/3] w-full overflow-hidden">
                  <SmartImage
                    src={src}
                    alt={`${article.name} close-up ${i + 1}`}
                    fit="cover"
                    fallbackLabel={article.name}
                    className="transition-transform duration-300 group-hover:scale-[1.04]"
                  />
                </div>
              </figure>
            ))}
          </div>
        )}

        <div className="mt-8 space-y-8">
          {(article.sections || []).map((s, i) => (
            <div key={i}>
              <h2 className="font-display text-xl font-semibold text-foreground">{s.heading}</h2>
              <p className="mt-2 leading-relaxed text-foreground/80">{s.body}</p>
            </div>
          ))}
        </div>

        {(article.usedIn || []).length > 0 && (
          <div className="mt-10 rounded-2xl border border-border bg-card p-5">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Ginagamit sa</h3>
            <ul className="mt-3 space-y-2">
              {article.usedIn.map((p) => (
                <li key={p} className="flex items-center gap-2 text-sm text-foreground/80">
                  <Check className="h-4 w-4 shrink-0 text-primary" />
                  {p}
                </li>
              ))}
            </ul>
            <Button asChild className="mt-4 bg-accent text-accent-foreground hover:bg-accent/90">
              <Link to="/#menu" data-testid="science-view-product">
                <ShoppingBag className="mr-2 h-4 w-4" /> Tingnan ang product
              </Link>
            </Button>
          </div>
        )}

        {(article.sources || []).length > 0 && (
          <div className="mt-10">
            <h3 className="font-display text-lg font-semibold text-foreground">Mga Sanggunian (Sources)</h3>
            <ul className="mt-3 space-y-2" data-testid="science-sources">
              {article.sources.map((s, i) => (
                <li key={i}>
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-start gap-1.5 text-sm text-primary hover:underline">
                    <ExternalLink className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{s.label || s.url}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="mt-8 rounded-lg border border-border bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">{SCIENCE_DISCLAIMER}</p>

        {related.length > 0 && (
          <div className="mt-12">
            <h3 className="font-display text-lg font-semibold text-foreground">Iba pang element</h3>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {related.map((a) => {
                const RIcon = getScienceIcon(a.icon || a.name);
                return (
                  <Link
                    key={a.slug}
                    to={`/science/${a.slug}`}
                    className="group rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/40"
                    data-testid={`science-related-${a.slug}`}
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/12 text-primary">
                      <RIcon className="h-5 w-5" />
                    </span>
                    <p className="mt-3 text-sm font-semibold text-foreground group-hover:text-primary">{a.name}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
